import type { Control } from "../core/Control.js";
import type { Size } from "../../common/geometry.js";
import type { Theme } from "../themes/Theme.js";
import { listNames, type WarningSink } from "./errors.js";
import type { AttrTable, AttrType, Attrs, ElementBase } from "./registry.js";
import {
  ALIGNMENTS,
  isAbsolute,
  parseAnchor,
  parseBoolean,
  parseColor,
  parseFont,
  parseInsets,
  parseLength,
  parseList,
  parseNumber,
  parseSize,
  parseTracks,
  paletteNames,
  toPixels,
  type Length,
} from "./values.js";
import type { XmlAttribute } from "./xml.js";

const DOCKS = ["none", "left", "top", "right", "bottom", "fill"] as const;

/**
 * Attributes every element takes.
 *
 * `width` and `height` are deliberately not `number`: they are the one place the dialect's whole
 * argument lives, and what they may say — `auto`, `2em`, `*`, `50%` — is the reason a layout
 * needs no coordinates. They are pulled out of the resolved attributes by `applySizing` rather
 * than handed to a constructor, because what they mean depends on what the parent is.
 */
export const CONTROL_ATTRS: AttrTable = {
  name: "string",
  class: "string",
  x: "number",
  y: "number",
  width: "length",
  height: "length",
  size: "size",
  visible: "boolean",
  enabled: "boolean",
  tabStop: "boolean",
  tabIndex: "number",
  anchor: "anchor",
  dock: DOCKS,
  fill: "boolean",
  hAlign: ALIGNMENTS,
  vAlign: ALIGNMENTS,
  align: ALIGNMENTS,
  margin: "insets",
  cursor: "string",
  tooltip: "string",
  /**
   * What a screen reader calls this.
   *
   * On the universal table rather than on each element: one line gives it to
   * all thirty-three of them, and a glyph button whose caption is a triangle
   * needs it wherever it turns up.
   */
  accessibleName: "string",
  /** An outline other than the rectangle: a preset name or path data in a 100 by 100 box. */
  shape: "string",
};

export const CONTAINER_ATTRS: AttrTable = {
  ...CONTROL_ATTRS,
  busy: "boolean",
  busyText: "string",
};

export const PANEL_ATTRS: AttrTable = {
  ...CONTAINER_ATTRS,
  style: ["none", "flat", "raised", "sunken"],
  background: "color",
  padding: "insets",
  clickThrough: "boolean",
  look: "string",
  lookColor: "colorRef",
  lit: "boolean",
};

export const LAYOUT_ATTRS: AttrTable = {
  ...PANEL_ATTRS,
  gap: "number",
  alignItems: ALIGNMENTS,
  autoSize: "boolean",
};

/** Cell placement, taken off any child of a Grid. */
export const GRID_CHILD_ATTRS: AttrTable = {
  row: "number",
  col: "number",
  column: "number",
  rowSpan: "number",
  colSpan: "number",
  columnSpan: "number",
};

const BASES: Record<ElementBase, AttrTable> = {
  control: CONTROL_ATTRS,
  container: CONTAINER_ATTRS,
  panel: PANEL_ATTRS,
  layout: LAYOUT_ATTRS,
};

/** The full vocabulary of one element: its own attributes plus its base class's. */
export function tableFor(base: ElementBase | undefined, own: AttrTable | undefined): AttrTable {
  return { ...BASES[base ?? "control"], ...GRID_CHILD_ATTRS, ...own };
}

export interface CoerceContext {
  theme: Theme;
  warn: WarningSink;
}

/**
 * Turn one attribute's text into a value, or report why it could not be turned.
 *
 * Everything here fails soft: a bad value is a warning and a skipped attribute, never a thrown
 * error. A layout is a hundred small statements and one of them being wrong is not a reason to
 * show the user nothing — the same view `parseGridLength` has always taken of an unreadable
 * track. Only structure — an element that does not exist, a handler that does not exist — is
 * worth refusing to load over.
 */
export function coerce(
  attribute: XmlAttribute,
  type: AttrType,
  ctx: CoerceContext,
): unknown | undefined {
  const { name, value } = attribute;
  const bad = (expected: string): undefined => {
    ctx.warn({ message: `"${name}" expected ${expected}, not "${value}"`, ...attribute });
    return undefined;
  };

  if (Array.isArray(type)) {
    const allowed = type as readonly string[];
    if (allowed.includes(value)) return value;
    return bad(listNames(allowed, allowed.length));
  }

  switch (type) {
    case "string":
    case "text":
      return value;
    case "boolean":
      return parseBoolean(value) ?? bad(`"true" or "false"`);
    case "number":
      return parseNumber(value, ctx.theme.metrics) ?? bad("a number");
    case "length": {
      const length = parseLength(value);
      return length ?? bad(`a length such as "120", "2em", "*", "50%" or "auto"`);
    }
    case "insets":
      return parseInsets(value, ctx.theme.metrics) ?? bad(`up to four numbers, as in "8 16"`);
    case "size":
      return parseSize(value, ctx.theme.metrics) ?? bad(`a size such as "640x480"`);
    case "color": {
      const color = parseColor(value, ctx.theme.palette);
      return color ?? bad(`a color such as "#3478f6" or ${listNames(paletteNames(ctx.theme.palette), 4)}`);
    }
    case "colorRef": {
      // Checked like a color, kept as written: a palette name stays a name, so whatever it is
      // handed to resolves it against the theme in force when it paints, not the one at load.
      const s = value.trim();
      if (parseColor(s, ctx.theme.palette)) return s;
      return bad(`a color such as "#3478f6" or ${listNames(paletteNames(ctx.theme.palette), 4)}`);
    }
    case "font":
      return parseFont(value, ctx.theme.metrics) ?? bad(`a font such as "bold 15 Consolas"`);
    case "list":
      return parseList(value);
    case "anchor":
      return parseAnchor(value) ?? bad(`edges such as "left top", or "all"`);
    case "tracks":
      return parseTracks(value, ctx.theme.metrics);
    case "date": {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? bad(`a date such as "2026-09-13"`) : date;
    }
  }
}

/**
 * What a `width` or `height` turned out to mean for one child.
 *
 * Split out because the answer depends on the parent: the same `*` is a weight inside a stack, a
 * stretch inside a grid cell and a stretch inside a plain container. The loader asks the parent
 * what to do with the relative ones and applies the rest itself.
 */
export interface Sizing {
  width: Length;
  height: Length;
}

export function sizingOf(attrs: Attrs): Sizing {
  const explicit = attrs["size"] as Size | undefined;
  if (explicit) {
    return {
      width: { kind: "px", value: explicit.width },
      height: { kind: "px", value: explicit.height },
    };
  }
  return {
    width: (attrs["width"] as Length | undefined) ?? { kind: "auto" },
    height: (attrs["height"] as Length | undefined) ?? { kind: "auto" },
  };
}

/**
 * Apply the absolute half of a sizing, and turn the rest into auto flags.
 *
 * An axis that says `auto` — which is every axis nobody wrote anything for — becomes an auto
 * flag, so the parent measures it. An axis in pixels or `em` becomes a number. Stars and
 * percentages are the parent's business and are left for the caller.
 */
export function applyAbsoluteSizing(control: Control, sizing: Sizing, theme: Theme): void {
  let width = control.width;
  let height = control.height;

  if (sizing.width.kind === "auto") control.autoWidth = true;
  else if (isAbsolute(sizing.width)) width = toPixels(sizing.width, theme.metrics);

  if (sizing.height.kind === "auto") control.autoHeight = true;
  else if (isAbsolute(sizing.height)) height = toPixels(sizing.height, theme.metrics);

  control.setSize(width, height);
}

/** Attribute names the loader consumes itself rather than passing to a constructor. */
export const LOADER_ATTRS = new Set([
  "class",
  "shape",
  "width",
  "height",
  "size",
  "fill",
  "gap",
  "alignItems",
  "row",
  "col",
  "column",
  "rowSpan",
  "colSpan",
  "columnSpan",
]);
